<script lang="ts" setup>
import { type RouteLocationRaw, useRouter } from 'vue-router';
import { computed, onMounted, useTemplateRef } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

const router = useRouter();

const props = defineProps<{
  to: RouteLocationRaw;
  preloadOnHover?: boolean;
  preloadOnVisible?: boolean;
}>();

const routerLinkElement = useTemplateRef<HTMLAnchorElement>('routerLinkElement');
let preloaded = false;
let navigatedOnMouseDown = false;
const href = computed(() => router.resolve(props.to).href);

onMounted(() => {
  if (!props.preloadOnVisible) {
    return;
  }
  useIntersectionObserver(routerLinkElement, (entries) => {
    if (!entries[0]) {
      return;
    }
    if (entries[0].isIntersecting) {
      preload();
    }
  });
});

const routesToPreload = computed<(() => Promise<void>)[]>(() => {
  if (!props.preloadOnHover && !props.preloadOnVisible) {
    return [];
  }
  const resolvedRouterTree = router.resolve(props.to);
  if (!resolvedRouterTree.matched.length) {
    return [];
  }
  return resolvedRouterTree.matched
    .filter((record) => !!record.components && Object.keys(record.components).length > 0)
    .map((record) => Object.values(record.components || {}))
    .flat()
    .filter((Component): Component is () => Promise<void> => {
      // eslint-disable-next-line
      return typeof Component === 'function' && (Component as any).cid === undefined;
    });
});

function onMouseEnter() {
  if (!props.preloadOnHover) {
    return;
  }
  preload();
}

function onMouseDown(e: MouseEvent) {
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button > 0) {
    return;
  }
  e.preventDefault();
  navigatedOnMouseDown = true;
  void router.push(props.to);
}

function onClick(e: MouseEvent) {
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button > 0) return;
  e.preventDefault();
  if (!navigatedOnMouseDown) void router.push(props.to);
  navigatedOnMouseDown = false;
}

function preload() {
  if (preloaded || !routesToPreload.value.length) {
    return;
  }
  preloaded = true;
  // wait for the browser to be idle before preloading
  if (window.requestIdleCallback) {
    window.requestIdleCallback(
      () => {
        routesToPreload.value.forEach((component) => component());
      },
      { timeout: 1000 },
    );
  } else {
    setTimeout(() => {
      routesToPreload.value.forEach((component) => component());
    }, 1000);
  }
}
</script>

<template>
  <a
    ref="routerLinkElement"
    :href
    @click="onClick"
    @mousedown="onMouseDown"
    @mouseenter="onMouseEnter"
  >
    <slot />
  </a>
</template>

<style lang="scss" scoped></style>
